/**
 * Pure element-level privacy logic, extracted from the SDK class so the rules
 * that decide whether an element is redacted can be unit-tested (and
 * mutation-tested) without a full DOM, in the same spirit as sessionLifecycle.ts
 * and recordingPaths.ts.
 *
 * rrweb already redacts elements carrying the block class / block selector from
 * session replay AND from the DOM snapshot (the heatmap background), because
 * both go through the same recorder. What it does NOT cover is the SDK's OWN
 * heatmap interaction capture (trackClick/trackHover/trackMouseMove), which
 * records click coordinates plus element text, id, and XPath. So a component the
 * customer blocked for replay still leaked its identity through a heatmap click.
 * These helpers let the interaction handlers consult the same block config, so
 * one `rr-block` hides an element from replay, the snapshot, and heatmaps alike.
 */

/**
 * Build the CSS selector that marks an element (or any ancestor) as redacted,
 * from the resolved privacy config. Mirrors what rrweb is given: the block class
 * (default "rr-block") and an optional block selector. Returns null when neither
 * is configured, so callers can skip the DOM walk entirely.
 */
export function buildBlockCaptureSelector(
  blockClass: string | undefined,
  blockSelector: string | undefined
): string | null {
  const parts: string[] = [];
  const cls = (blockClass || "").trim();
  if (cls) parts.push("." + cls);
  const sel = (blockSelector || "").trim();
  if (sel) parts.push(sel);
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * Minimal shape of what this check needs from a DOM element: `closest`, so the
 * check matches an element OR any ancestor (blocking a container blocks its
 * whole subtree, exactly as rrweb treats the block class).
 */
export interface Closestable {
  closest(selector: string): unknown;
}

/**
 * Whether interaction capture for `el` must be suppressed because the element,
 * or an ancestor, is inside a redacted region. No selector (nothing configured)
 * or no element means nothing is blocked.
 */
export function isElementCaptureBlocked(
  el: Closestable | null | undefined,
  selector: string | null
): boolean {
  if (!el || !selector) return false;
  try {
    return el.closest(selector) != null;
  } catch {
    // An invalid custom blockSelector must never throw inside an event handler;
    // fail OPEN to today's behavior (capture) rather than crash the handler.
    return false;
  }
}
